import React, { ReactNode } from 'react';
import Breadcrumb from '../../Breadcrumb';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import CustomTabv2 from '../../custom/CustomTabv2';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { RootState } from '../../../redux/store';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { StreamState } from '../../../redux/reducers/StreamReducer';
import { changeStream, changeStreamDependencies, changeStreamDirty, changeStreamIsUpdate, clearStream } from '../../../redux/actions/StreamActions';
import ComponentName from '../../../constant/ComponentName';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { StreamJsonModel } from '../../../domain/stream/stream/StreamDomain';
import StreamEditorComponent from './StreamEditorComponent';
import StreamAccessComponent from '../access/StreamAccessComponent';
import StreamService from '../../../service/StreamService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import CustomConfirmDialog from '../../custom/CustomConfirmDialog';
import LanguageUtil from '../../../util/LanguageUtil';
import CustomLoading from '../../custom/CustomLoading';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import { clearPage } from '../../../redux/actions/PageActions';
import { ComponentDependency } from '../../../domain/ui/ComponentMetadata';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../../ComponentErrorLog';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;
import and = query.and;

class StreamEditorContainer extends StandardEditorContainerComponent<Props, State, StreamJsonModel> {
  public state: State = new State();

  private streamSubscription: subscribe.Subscription | undefined;
  private enrichmentSubscription: subscribe.Subscription | undefined;
  private transactionSubscription: subscribe.Subscription | undefined;
  private componentDependencySubscription: subscribe.Subscription | undefined;
  private structSubscription: subscribe.Subscription | undefined;

  private computation: subscribe.Computation | undefined;
  private streamComputation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly streamService: StreamService = ServiceContainer.getInstance().get(ServiceNames.STREAM);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.STREAM,
      icon: 'stream',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      stream: { stream, isUpdate },
    } = this.props;
    const { loading, streamLoading, undeployDialogVisible } = this.state;
    if (loading || streamLoading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomConfirmDialog
          visible={undeployDialogVisible}
          onHide={this.hideUndeployDialog}
          icon={<img src={'/icons/error.svg'} alt='error' className='w-[172px] h-[172px] flex items-center justify-center' />}
          title={LanguageUtil.get({
            id: 'messages.confirm.undeploy1',
            defaultMessage: 'Are you sure you want to undeploy?',
          })}
          description={LanguageUtil.get({
            id: 'messages.confirm.undeployDescription',
            defaultMessage: 'If you undeploy, all data belonging to this component will be deleted.',
          })}
          cancelLabel={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
          confirmLabel={LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          onConfirm={this.confirmUndeploy}
        />
        <CustomEditorItemComponent
          name={stream.name}
          description={stream.description}
          status={stream?.stage || 'DRAFT'}
          component={ComponentName.STREAM}
          isUpdate={isUpdate}
          icon={'stream'}
          onChangeName={(name) => this.onChangeName(name)}
          onChangeDescription={(description) => this.onChangeDescription(description)}
          onSave={() => this.onSave()}
          onRemove={(e) => this.onRemove(e)}
          onDeploy={(e) => this.onDeploy(e)}
          onUndeploy={(e) => this.onUndeploy(e)}
          disableSave={!RoleHelper.canEdit('Stream')}
          disableRemove={!RoleHelper.canEdit('Stream')}
          disableNameChange={!RoleHelper.canEdit('Stream')}
          disableDescriptionChange={!RoleHelper.canEdit('Stream')}
          disableDeploy={!RoleHelper.canDeploy('Stream')}
          disableUndeploy={!RoleHelper.canDeploy('Stream')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full'}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearStream } = this.props;
    clearStream();
    await super.componentWillUnmount();
  }

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  protected _startTracker = async () => {
    const variableId = this.getItemIdFromRoute();
    if (variableId == undefined) {
      this.setState({ loading: false });
    }
    const streamQuery = query.QueryBuilder.select().from(CollectionNames.STREAMS).where(eq('name', variableId));
    const enrichmentQuery: query.Query = query.QueryBuilder.select()
      .from(CollectionNames.ENRICHMENTS)
      .select('id', 'name', 'properties', 'type', 'status')
      .build();
    const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
    this.streamSubscription = subscribe.Formica.subscribe(CollectionNames.STREAMS, streamQuery.build());
    this.enrichmentSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, enrichmentQuery);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery);
    this.structSubscription = subscribe.Formica.subscribe(
      CollectionNames.DATA_STRUCTURES,
      query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build(),
    );
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.enrichmentSubscription, this.transactionSubscription, this.structSubscription]);
    this.streamComputation = subscribe.Tracker.autorun(this.streamTracker, [this.streamSubscription]);
    if (variableId) {
      const componentDependencyQuery: query.Query = query.QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('meta.stream', variableId), eq('dependentType', ComponentName.STREAM_COLUMN)))
        .build();
      this.componentDependencySubscription = subscribe.Formica.subscribe(CollectionNames.COMPONENT_DEPENDENCY, componentDependencyQuery);
      this.computation.addSubscription(this.componentDependencySubscription);
    }
  };

  protected tracker = async () => {
    if (
      this.streamSubscription?.ready() &&
      this.structSubscription?.ready() &&
      this.enrichmentSubscription?.ready() &&
      this.transactionSubscription?.ready() &&
      (this.componentDependencySubscription == undefined || this.componentDependencySubscription?.ready())
    ) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      this.setState({ transactions: transactions, loading: false });
    }
  };

  private streamTracker = async () => {
    if (this.streamSubscription?.ready()) {
      const { changeStreamIsUpdate, changeStream, changeStreamDependencies, changeStreamDirty } = this.props;
      const variableId = this.getItemIdFromRoute();
      changeStreamIsUpdate(variableId != undefined);
      if (variableId == undefined) {
        this.setState({ streamLoading: false });
        RoleHelper.setCurrentAccess('Edit');
        changeStreamDirty(false);
        return;
      }
      const streamQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.STREAMS).where(eq('name', variableId)).build();
      const componentDependencyQuery: query.Query = query.QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('meta.stream', variableId), eq('dependentType', ComponentName.STREAM_COLUMN)))
        .build();
      const componentDependencies: ComponentDependency[] = await this.repository.findMany(componentDependencyQuery);
      const stream: StreamJsonModel | undefined = await this.repository.find(streamQuery);
      if (stream == undefined) return;
      changeStreamDependencies(componentDependencies);
      changeStream(stream);
      RoleHelper.setCurrentAccess(stream);
      changeStreamDirty(false);
      this.setState({ streamLoading: false, streamUpdated: this.state.streamUpdated + 1 });
    }
  };

  protected _stopTracker = async () => {
    await this.streamSubscription?.stop();
    await this.enrichmentSubscription?.stop();
    await this.transactionSubscription?.stop();
    await this.componentDependencySubscription?.stop();
    await this.structSubscription?.stop();
    this.streamComputation?.stop();
    this.computation?.stop();
  };

  protected getItemIdFromRoute = () => {
    const { changeStreamIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeStreamIsUpdate(id != undefined);
    return id;
  };

  private onChangeName = (name: string) => {
    const {
      stream: { stream },
      changeStream,
    } = this.props;
    if (isStringValid(name)) {
      stream.name = name.trim();
      changeStream(stream);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      stream: { stream },
      changeStream,
    } = this.props;
    stream.description = description;
    changeStream(stream);
  };

  private onSave = async () => {
    const {
      stream: { stream, isUpdate },
      changeStreamIsUpdate,
      lastCategory,
    } = this.props;
    const isSaved = await this.streamService.save(stream, isUpdate, lastCategory);
    if (isSaved) {
      changeStreamIsUpdate(true);
      if (!isUpdate) await this.switchToUpdate();
    }
    this.setState({ newColumnIds: [] });
  };

  private onRemove = async (e: any) => {
    const {
      stream: { stream },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.streamService.delete(stream.name);
        if (success) {
          navigate(`/stream`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.delete1', defaultMessage: 'Are you sure you want to remove?' }),
    });
  };

  private onDeploy = (e: any) => {
    const {
      stream: { stream },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        if (this.streamService.warnIfColumnsMissing(stream.columns)) {
          return;
        }
        const saved = await this.streamService.save(stream, true, {} as EnrichedComponentCategory);
        if (!saved) {
          return;
        }
        await this.streamService.deploy(stream);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
    });
  };

  private onUndeploy = async (e: any) => {
    e?.preventDefault?.();
    this.setState({ undeployDialogVisible: true });
  };

  private hideUndeployDialog = () => {
    this.setState({ undeployDialogVisible: false });
  };

  private confirmUndeploy = async () => {
    const {
      stream: { stream },
    } = this.props;
    this.setState({ undeployDialogVisible: false });
    await confirmUndeployWithImpact(ComponentName.STREAM, [stream.name], () => this.streamService.undeploy(stream.name));
  };

  private switchToUpdate = async () => {
    const {
      stream: { stream },
      enterComponent,
    } = this.props;
    if (stream.name == '') return;
    const {
      router: { navigate, params },
    } = this.props;
    enterComponent({ component: ComponentName.STREAM, id: stream.name });
    navigate(`/stream${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${stream.name}`, { replace: true });
    await this.resubscribeTracker();
  };

  protected getTabs = () => {
    const { transactions, streamUpdated, newColumnIds } = this.state;
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: `common.tabs.editor`, defaultMessage: 'Editor' }),
        content: (
          <StreamEditorComponent
            transactions={transactions}
            streamUpdated={streamUpdated}
            newColumnIds={newColumnIds}
            onNewColumnChanged={() => {
              this.setState({ newColumnIds: [...this.state.newColumnIds] });
            }}
          />
        ),
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: `common.tabs.access`, defaultMessage: 'Access' }),
        content: <StreamAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.STREAM, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: `common.tabs.audit`, defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.STREAM} documentName={this.props.stream.stream.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.STREAM, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: LanguageUtil.get({ id: `common.tabs.error`, defaultMessage: 'Error' }),
        content: <ComponentErrorLog component={ComponentName.STREAM} documentName={this.props.stream.stream.name} />,
        visible: RoleHelper.canManage(ComponentName.STREAM, true),
      },
    ];
  };

  protected changeItem = (item: StreamJsonModel) => {
    const { changeStream } = this.props;
    changeStream(item);
    this.setState({ streamLoading: false });
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
  streamLoading: boolean = true;
  streamUpdated: number = 0;
  transactions: TransactionJsonModel[] = [];
  newColumnIds: string[] = [];
  undeployDialogVisible: boolean = false;
}

const mapStateToProps = (state: RootState) => ({
  stream: state.stream as StreamState,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeStream,
  changeStreamIsUpdate,
  clearStream,
  changeStreamDependencies,
  clearPage,
  changeStreamDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(StreamEditorContainer));
